// Catalog.ID Client — Key Rotation View
// ============================================================================
// Standalone view shown when a locked account with approved unlock status logs in.
// Flow: enter supersecret → decrypt key bag → generate new keypairs → rotate → unlock.

import { createElement } from '@catalog-shell';
import * as aes from '../crypto/aes';
import * as accountApi from '../api/account';
import { formatApiError } from '../api/client';
import { kemKeygen } from '../crypto/kem';
import { signHybrid, wrapperToJson } from '../crypto/signing';
import { decryptKeyBag, encryptKeyBag, rotatePersonalEncryptionKeys, getKeyBagEntry } from '../crypto/keybag';
import type { KeyBag } from '../crypto/keybag';
import { createSupersecretTextarea } from '../components/supersecret-textarea';
import { session } from '../state/session';

export interface KeyRotationViewCallbacks {
    onComplete: () => void;
}

export function renderKeyRotationView(container: HTMLElement, _callbacks: KeyRotationViewCallbacks): void {
    container.innerHTML = '';

    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card' });

    card.appendChild(createElement('h2', {
        className: 'cid-auth-title',
        textContent: 'Key Rotation',
    }));
    card.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'Your account has been approved for key rotation after lockdown. Enter your supersecret to generate new encryption keys.',
    }));

    const errorArea = createElement('div', { className: 'cid-auth-error' });
    card.appendChild(errorArea);

    const form = createElement('div', { className: 'cid-auth-form' });

    // id `cid-rotation-supersecret` is referenced by e2e tests — preserve it.
    const ss = createSupersecretTextarea({ id: 'cid-rotation-supersecret', autoFocus: true });
    form.appendChild(ss.element);

    const statusEl = createElement('div', {
        className: 'cid-wizard-text',
        style: { marginTop: '12px', minHeight: '40px' },
    });
    form.appendChild(statusEl);

    const rotateBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Rotate Keys',
    }) as HTMLButtonElement;

    function showError(msg: string): void {
        errorArea.textContent = msg;
        errorArea.classList.add('cid-auth-error--visible');
    }
    function clearError(): void {
        errorArea.textContent = '';
        errorArea.classList.remove('cid-auth-error--visible');
    }
    function setStatus(msg: string): void {
        statusEl.textContent = msg;
    }

    rotateBtn.disabled = true;
    ss.onValidityChange(valid => { rotateBtn.disabled = !valid; });

    rotateBtn.addEventListener('click', async () => {
        clearError();
        if (!ss.isValid()) return;
        const supersecret = ss.getValue();

        rotateBtn.setAttribute('disabled', '');
        rotateBtn.textContent = 'Rotating...';

        try {
            // Step 1: Fetch and decrypt key bag
            setStatus('Decrypting key bag...');
            const keyBagResp = await accountApi.getKeyBag();
            if (!keyBagResp.entries || keyBagResp.entries.length === 0) {
                throw new Error('No key bag found on account');
            }

            let bag: KeyBag;
            try {
                bag = await decryptKeyBag(keyBagResp, supersecret);
            } catch {
                throw new Error('Invalid supersecret — could not decrypt key bag');
            }

            // Step 2: Generate new hybrid KEM keypairs
            setStatus('Generating new encryption keys...');
            const newAttrKem = kemKeygen();
            const newMsgKem = kemKeygen();
            // efs_encrypt is rotated too: a compromise-driven lockdown must not
            // leave the EFS key live (EFS WP §12.6).
            const newEfsKem = kemKeygen();

            // Step 3: Update key bag — revoke old keys, add new ones. Every
            // entry NOT in PERSONAL_ROTATED_KEY_IDS (main_signing_key, the chat
            // identity keys chat_encrypt / chat_sign, and the mail identity keys
            // mail_encrypt / mail_sign) is carried through untouched: the server
            // replaces the stored bag with exactly what we submit here, so a
            // dropped entry is destroyed, not stale. Chat- and mail-key rotation
            // are their own ceremonies (chat migration plan O7; mail §5.1 — every
            // archived envelope is wrapped to a generation of mail_encrypt, and
            // decrypting old mail rides keybag retention of the rotated-out
            // private entries).
            bag = rotatePersonalEncryptionKeys(bag, {
                attr_encrypt: aes.toBase64(newAttrKem.secretKey),
                msg_encrypt: aes.toBase64(newMsgKem.secretKey),
                efs_encrypt: aes.toBase64(newEfsKem.secretKey),
            });

            // Step 4: Re-encrypt key bag with supersecret
            setStatus('Encrypting updated key bag...');
            const encrypted = await encryptKeyBag(bag, supersecret);

            // Step 5: Prove supersecret knowledge (H-3) — request a challenge and
            // sign it with the *current* (pre-rotation) main_signing_key. The private
            // key is in the decrypted key bag, so a caller who doesn't hold the
            // supersecret can't produce a valid signature.
            setStatus('Signing rotation challenge...');
            const signingEntry = getKeyBagEntry(bag, 'main_signing_key');
            if (!signingEntry) {
                throw new Error('No main_signing_key in key bag — cannot prove rotation authority');
            }
            const challengeResp = await accountApi.getRotateKeysChallenge();
            const cid = session.getCid();
            if (!cid) {
                throw new Error('No CID in session — cannot sign challenge');
            }
            const challengeMessage = aes.encodeUtf8(
                `rotate-keys\n${cid}\n${challengeResp.challenge}`);
            const wrapper = signHybrid(
                challengeMessage,
                aes.fromBase64(signingEntry.privateKey));
            const challengeSignature = wrapperToJson(wrapper);

            // Step 6: Submit to server
            setStatus('Submitting key rotation to server...');
            const result = await accountApi.rotateKeys({
                newAttrEncryptPublicKey: aes.toBase64(newAttrKem.publicKey),
                newMsgEncryptPublicKey: aes.toBase64(newMsgKem.publicKey),
                newEfsEncryptPublicKey: aes.toBase64(newEfsKem.publicKey),
                publicKeyAlgorithm: 'X25519-MLKem1024',
                keyBagEntries: encrypted.entries,
                keySalt: encrypted.keySalt,
                challengeSignature,
            });

            setStatus(`Keys rotated successfully. ${result.claimKeysRewrapped} claim keys re-wrapped.`);

            // Clear session (account is unlocked, all sessions revoked — need fresh login)
            session.clear();

            // Show success and redirect after brief delay
            rotateBtn.textContent = 'Done — redirecting to login...';
            setTimeout(() => {
                window.location.href = '/';
            }, 2000);

        } catch (err) {
            rotateBtn.removeAttribute('disabled');
            rotateBtn.textContent = 'Rotate Keys';
            setStatus('');
            showError(formatApiError(err, 'Key rotation failed'));
        }
    });

    form.appendChild(rotateBtn);
    card.appendChild(form);
    wrapper.appendChild(card);
    container.appendChild(wrapper);
}
